/**
 * tutorStudentActivitiesViewModel — `(reduxState, studentId) → props` de l'onglet Activités de la fiche
 * d'un élève (patron A).
 *
 * Ce que lit `TodoListScreen.js` côté tuteur, l'élève résolu dans `currentUser.students` par son id
 * (`StudentTabsNavigator.js:46-48`), où le réducteur de `getStudent` range ses activités :
 *  - « À faire » : `todo_activities`, dans l'ordre servi (la liste se range au doigt, elle ne trie pas) ;
 *  - « Faites aujourd'hui » : celles du jour (le titre que la fiche pose). Les deux faites d'avant que
 *    `getTodaysFinishedActivities` ajoutait passent avec les autres terminées, dépliées dans l'onglet ;
 *  - le bloc des faites se montre dès qu'il y en a, ou que l'élève a déjà travaillé (`xp > 0`), comme
 *    le legacy (`TodoListScreen:711`) : c'est le chemin vers ses terminées ;
 *  - les terminées (`history.activities`, pages de `getStudentFinishedActivities`) : celles d'avant
 *    aujourd'hui, celles du jour étant déjà au-dessus.
 *
 * Le chargement est celui de `getStudent` (`request` de `TodoListScreen`) : une liste vide pendant
 * qu'elle se lit montre sa forme, jamais « Lance une activité ! ».
 *
 * Une fabrique : la fiche peut être montée deux fois à la fois (au large, puis poussée), chacune garde
 * sa mémoïsation (`ports/CLAUDE.md` § view-models).
 */
import moment from "moment"
import { createSelector } from "reselect"

import { toUIActivities, type ApiActivity } from "../../../adapters/parent/Activities/activityAdapter"
import { type UIActivity } from "../../../ui/types"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type TutorStudentActivitiesViewModel = {
    activities: UIActivity[]
    /** Les faites du jour. */
    doneToday: UIActivity[]
    /** Le bloc des faites est montré. */
    showDone: boolean
    /** Les terminées d'avant aujourd'hui, pages chargées. */
    history: UIActivity[]
    /** Les terminées d'avant aujourd'hui que le serveur annonce, pages pas encore chargées comprises. */
    historyTotal: number
    /** Une page suivante des terminées se lit. */
    historyLoadingMore: boolean
    /** L'erreur de la page des terminées, une fois ouvertes. */
    historyError: unknown | null
    loading: boolean
    refreshing: boolean
    error: unknown | null
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

const isToday = (activity: ApiActivity) => moment(activity?.finished_at).isSame(moment(), "day")
const byMostRecent = (a: ApiActivity, b: ApiActivity) => moment(b.finished_at).diff(moment(a.finished_at))

/** Les faites du jour, les plus récentes en tête (`getTodaysFinishedActivities`, sa première moitié). */
export function finishedToday(activities: ApiActivity[] | null | undefined): ApiActivity[] {
    return (activities ?? []).filter(isToday).sort(byMostRecent)
}

export function makeTutorStudentActivitiesViewModel() {
    return createSelector(
        [
            (state: ReduxState, studentId: string | number | null) =>
                studentId == null ? null : selectDisplayedStudent(state, studentId),
            (state: ReduxState) => state?.data?.staticData?.school_subjects,
            (state: ReduxState) => state?.api?.getStudent,
            (state: ReduxState) => state?.api?.getStudentFinishedActivities,
        ],
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        (student: any, subjects: any, studentReq: any, historyReq: any): TutorStudentActivitiesViewModel => {
            const activities = toUIActivities(student?.todo_activities, subjects ?? [])
            const today = finishedToday(student?.activities)
            const doneToday = toUIActivities(today, subjects ?? [])
            const historyRows = ((student?.history?.activities ?? []) as ApiActivity[]).filter(
                (activity) => !isToday(activity)
            )
            const history = toUIActivities(historyRows, subjects ?? [])
            // Le chargement de la fiche ne dit rien d'un autre élève lu en même temps.
            const ownRead =
                studentReq?.routeParams?.id == null ||
                (student?.id != null && String(studentReq.routeParams.id) === String(student.id))
            const requesting = !!studentReq?.request && ownRead
            const hasRows = activities.length > 0 || doneToday.length > 0
            const historyPage = Number(historyReq?.urlParams?.page ?? 1)
            const error = hasRows || !ownRead ? null : (studentReq?.error ?? null)
            const total = Number(student?.history?.activities_meta?.total ?? student?.history?.activities?.length ?? 0)
            return {
                activities,
                doneToday,
                showDone: doneToday.length > 0 || (student?.xp ?? 0) > 0 || history.length > 0,
                history,
                // Celles du jour sont au-dessus : le lien compte les autres.
                historyTotal: Math.max(0, total - doneToday.length),
                historyLoadingMore: !!historyReq?.request && historyPage > 1,
                historyError: historyReq?.error ?? null,
                loading: error == null && (student == null || requesting) && !hasRows,
                refreshing: requesting && hasRows,
                error,
            }
        }
    )
}
